import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, SearchInput } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import FieldRow from "./FieldRow";
import MappingOverlay from "./MappingOverlay";
import EntityFieldEditDialog from "./EntityFieldEditDialog";
import EntityObjectEditDialog from "./EntityObjectEditDialog";
import TransformToolkitDialog from "./TransformToolkitDialog";
import AiMappingConfirmDialog from "./AiMappingConfirmDialog";
import {
  asArray,
  buildEmptyFieldMap,
  draftKey,
  EMPTY_GUID,
  mapDiff,
  normalizeField,
  ensureUniqueMapItemIds,
  rehydrateDraftMap,
  replaceMapping,
  searchFieldMatch,
  toDraftMap,
} from "./utils";
import "./entityFieldMapDesigner.scss";

const cloneMap = (map) => ({
  ...map,
  items: asArray(map?.items).map((x) => ({ ...x })),
});

const cloneFields = (fields) => asArray(fields).map((field) => ({ ...field }));

const sameIdOrder = (left, right) => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};

const reorderByIdList = (fields, idOrder) => {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const ordered = idOrder.map((id) => byId.get(id)).filter(Boolean);
  const used = new Set(ordered.map((field) => field.id));
  const extras = fields.filter((field) => !used.has(field.id));
  return [...ordered, ...extras];
};

const typeKey = (field) => String(field?.dataTypeId || field?.dataTypeName || "").toLowerCase();
const isStringType = (field) => {
  const key = typeKey(field);
  return key === "string" || key === "system.string" || key.endsWith(".string");
};

const isDeletedEdge = (edge) => edge?.isDeleted === true;
const isInactiveEdge = (edge) => edge?.isActive === false && !isDeletedEdge(edge);
const isActiveEdge = (edge) => edge?.isActive !== false && !isDeletedEdge(edge);
const hasRenderableSource = (edge, sourceFieldById) =>
  edge?.isConstant === true || edge?.sourceFieldId === EMPTY_GUID || sourceFieldById.has(edge?.sourceFieldId);
const hasRenderableEntity = (edge, entityFieldById) => !!edge?.entityFieldId && entityFieldById.has(edge.entityFieldId);

const buildMappingSearchLabel = (edge, sourceFieldById, entityFieldById) => {
  const sourceField = edge?.isConstant ? null : sourceFieldById.get(edge?.sourceFieldId);
  const entityField = entityFieldById.get(edge?.entityFieldId);
  const sourceLabel = edge?.isConstant
    ? "(Constant)"
    : (sourceField?.displayName || sourceField?.fieldName || edge?.sourceFieldId || "Unknown Source");
  const entityLabel = entityField?.displayName || entityField?.fieldName || edge?.entityFieldId || "Unknown Entity";
  const stateLabel = isActiveEdge(edge) ? "Active" : "Inactive";
  return `${sourceLabel} -> ${entityLabel} [${stateLabel}]`;
};

const buildSearchSelectMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  DataJson: dataJson,
  PlaceHolder: placeHolder,
});

const edgeInvalid = (edge, sourceById, entityById) => {
  const sourceField = sourceById.get(edge.sourceFieldId);
  const entityField = entityById.get(edge.entityFieldId);
  if (!entityField) return true;

  const steps = asArray(edge?.transformSteps);
  const hasTransform = steps.length > 0 || !!String(edge.transformKey || "").trim() || !!String(edge.transformCode || "").trim();
  const customInvalid =
    steps.some((step) => String(step?.kind || step?.key || "").toLowerCase() === "custom" && !String(step?.code || "").trim()) ||
    (String(edge.transformKey || "").toLowerCase() === "custom" && !String(edge.transformCode || "").trim());
  if (customInvalid) return true;
  if (edge?.isConstant) return false;

  if (!sourceField) return true;
  if (isStringType(entityField)) return false;
  const sameType = typeKey(sourceField) === typeKey(entityField);
  if (!sameType && !hasTransform) return true;
  return false;
};

const compactFieldForAi = (field) => ({
  id: field?.id || null,
  fieldName: field?.fieldName || "",
  displayName: field?.displayName || "",
  dataTypeName: field?.dataTypeName || "",
  description: field?.description || "",
  aiPromptContext: field?.aiPromptContext || "",
  isRequired: field?.raw?.IsRequired === true || field?.isRequired === true,
});

const compactEdgeForAi = (edge, sourceFieldById, entityFieldById) => {
  const sourceField = edge?.isConstant ? null : sourceFieldById.get(edge?.sourceFieldId);
  const entityField = entityFieldById.get(edge?.entityFieldId);
  return {
    id: edge?.id || null,
    sourceObjectId: edge?.sourceObjectId || null,
    sourceFieldId: edge?.sourceFieldId || null,
    sourceFieldName: sourceField?.fieldName || "",
    sourceFieldDisplayName: sourceField?.displayName || "",
    entityFieldId: edge?.entityFieldId || null,
    entityFieldName: entityField?.fieldName || "",
    entityFieldDisplayName: entityField?.displayName || "",
    isConstant: edge?.isConstant === true,
    constantValue: edge?.constantValue || "",
    transformDisplayName: edge?.transformDisplayName || "",
    transformCode: edge?.transformCode || "",
    transformSteps: asArray(edge?.transformSteps).map((step) => ({
      kind: step?.kind || "",
      displayName: step?.displayName || "",
      key: step?.key || null,
      code: step?.code || "",
      isActive: step?.isActive !== false,
    })),
    importance: edge?.importance,
    isActive: edge?.isActive !== false,
  };
};

const EntityFieldMapDesigner = ({
  entityObject,
  sourceObjects,
  initialMapsBySourceId,
  dataTypes,
  fieldItemMetadata,
  entityObjectItemMetadata,
  sourceObjectFieldMapItemMetadata,
  sourceObjectFieldTransformScriptItemMetadata,
  lookupSourceObjects,
  lookupEntityObjects,
  transformCatalog,
  allowMultiSource = false,
  onSaveMap,
  onEntityFieldUpdate,
  onAiSuggestForField,
  onAiSuggestForEntityField,
  onAiContextChange,
  aiDraftUpdate,
}) => {
  const [activeSourceId, setActiveSourceId] = useState(asArray(sourceObjects)[0]?.id || "");
  const [mapsBySourceId, setMapsBySourceId] = useState({});
  const [savedBySourceId, setSavedBySourceId] = useState({});
  const [searchSource, setSearchSource] = useState("");
  const [searchEntity, setSearchEntity] = useState("");
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [transformEdgeId, setTransformEdgeId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [editEntityObjectOpen, setEditEntityObjectOpen] = useState(false);
  const [entityFields, setEntityFields] = useState(asArray(entityObject?.fields));
  const [savedEntityFields, setSavedEntityFields] = useState(cloneFields(entityObject?.fields));
  const [entityObjectDraftRaw, setEntityObjectDraftRaw] = useState(entityObject?.raw || {});
  const [savedEntityObjectRaw, setSavedEntityObjectRaw] = useState(entityObject?.raw || {});
  const [pendingFieldPatches, setPendingFieldPatches] = useState({});
  const [savedEntityFieldOrder, setSavedEntityFieldOrder] = useState(asArray(entityObject?.fields).map((field) => field.id));
  const [aiConfirm, setAiConfirm] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [reorderEntityFieldId, setReorderEntityFieldId] = useState(null);
  const [layout, setLayout] = useState({});
  const [aiFieldLoadingTarget, setAiFieldLoadingTarget] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [showArchivedMappings, setShowArchivedMappings] = useState(false);
  const [selectedSearchSourceId, setSelectedSearchSourceId] = useState("");
  const [selectedSearchEdgeId, setSelectedSearchEdgeId] = useState("");
  const [pendingOpenMapping, setPendingOpenMapping] = useState(null);
  const [pendingSelectMapping, setPendingSelectMapping] = useState(null);
  const isSingleSourceOnly = useMemo(() => {
    const raw = entityObject?.raw || {};
    return (
      raw?.SourceObjectOfRecordOnly === true ||
      entityObject?.sourceObjectOfRecordOnly === true
    );
  }, [entityObject]);

  const sourceRowRefs = useRef({});
  const entityRowRefs = useRef({});
  const canvasRef = useRef(null);
  const gutterRef = useRef(null);
  const lastAiContextRef = useRef("");
  const lastAiDraftUpdateRef = useRef(null);

  useEffect(() => {
    setEntityFields(asArray(entityObject?.fields));
    setSavedEntityFields(cloneFields(entityObject?.fields));
    setEntityObjectDraftRaw(entityObject?.raw || {});
    setSavedEntityObjectRaw(entityObject?.raw || {});
    setPendingFieldPatches({});
    setSavedEntityFieldOrder(asArray(entityObject?.fields).map((field) => field.id));
  }, [entityObject]);

  useEffect(() => {
    const nextMaps = {};
    const nextSaved = {};
    asArray(sourceObjects).forEach((source) => {
      const initialMap = initialMapsBySourceId?.[source.id] || buildEmptyFieldMap(entityObject?.id, source.id);
      const storageKey = draftKey(entityObject?.id, source.id, initialMap.id || "new");
      const draftRaw = localStorage.getItem(storageKey);
      let mapToUse = cloneMap(initialMap);
      if (draftRaw) {
        try {
          mapToUse = rehydrateDraftMap(JSON.parse(draftRaw), initialMap);
          setDraftRestored(true);
        } catch {
          mapToUse = cloneMap(initialMap);
        }
      }
      nextMaps[source.id] = mapToUse;
      nextSaved[source.id] = cloneMap(initialMap);
    });
    setMapsBySourceId(nextMaps);
    setSavedBySourceId(nextSaved);
  }, [sourceObjects, initialMapsBySourceId, entityObject?.id]);

  const sourceFieldById = useMemo(() => {
    const activeSource = asArray(sourceObjects).find((x) => x.id === activeSourceId);
    return new Map(asArray(activeSource?.fields).map((x) => [x.id, x]));
  }, [sourceObjects, activeSourceId]);

  const entityFieldById = useMemo(() => new Map(asArray(entityFields).map((x) => [x.id, x])), [entityFields]);

  const activeSource = useMemo(() => asArray(sourceObjects).find((x) => x.id === activeSourceId), [sourceObjects, activeSourceId]);
  const activeMap = mapsBySourceId[activeSourceId] || buildEmptyFieldMap(entityObject?.id, activeSourceId);
  const activeEdges = useMemo(() => asArray(activeMap?.items).filter(isActiveEdge), [activeMap]);
  const mapHasChanges = useCallback(
    (sourceId) => {
      const current = mapsBySourceId[sourceId] || buildEmptyFieldMap(entityObject?.id, sourceId);
      const saved = savedBySourceId[sourceId];
      return !saved ? asArray(current?.items).length > 0 : JSON.stringify(current) !== JSON.stringify(saved);
    },
    [mapsBySourceId, savedBySourceId, entityObject?.id]
  );
  const changedMapSourceIds = useMemo(
    () =>
      asArray(sourceObjects)
        .map((source) => source?.id)
        .filter((id) => !!id && mapHasChanges(id)),
    [sourceObjects, mapHasChanges]
  );
  const hasEntityOrderChanges = useMemo(() => {
    const currentOrder = asArray(entityFields).map((field) => field.id);
    return !sameIdOrder(currentOrder, savedEntityFieldOrder);
  }, [entityFields, savedEntityFieldOrder]);
  const hasEntityFieldChanges = useMemo(() => {
    return JSON.stringify(asArray(entityFields)) !== JSON.stringify(asArray(savedEntityFields));
  }, [entityFields, savedEntityFields]);
  const hasEntityObjectChanges = useMemo(() => {
    return JSON.stringify(entityObjectDraftRaw || {}) !== JSON.stringify(savedEntityObjectRaw || {});
  }, [entityObjectDraftRaw, savedEntityObjectRaw]);
  const hasUnsavedChanges = useMemo(() => {
    const hasMapChanges = changedMapSourceIds.length > 0;
    return hasMapChanges || hasEntityOrderChanges || hasEntityFieldChanges || hasEntityObjectChanges;
  }, [changedMapSourceIds, hasEntityOrderChanges, hasEntityFieldChanges, hasEntityObjectChanges]);
  const hasDraftAvailable = useMemo(() => {
    const hasMapChanges = changedMapSourceIds.length > 0;
    return hasMapChanges || hasEntityOrderChanges || hasEntityFieldChanges || hasEntityObjectChanges;
  }, [changedMapSourceIds, hasEntityOrderChanges, hasEntityFieldChanges, hasEntityObjectChanges]);
  const hasMappingsToClear = useMemo(() => asArray(activeMap?.items).length > 0, [activeMap]);

  const sourceFields = useMemo(() => asArray(activeSource?.fields).filter((x) => searchFieldMatch(x, searchSource)), [activeSource, searchSource]);
  const filteredEntityFields = useMemo(() => asArray(entityFields).filter((x) => searchFieldMatch(x, searchEntity)), [entityFields, searchEntity]);
  const isEntitySearchActive = useMemo(() => String(searchEntity || "").trim().length > 0, [searchEntity]);

  const invalidEdges = useMemo(
    () => activeEdges.filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById)),
    [activeEdges, sourceFieldById, entityFieldById]
  );
  const selectedMappingEdge = useMemo(
    () => activeEdges.find((edge) => edge.id === selectedEdgeId) || null,
    [activeEdges, selectedEdgeId]
  );

  const aiDesignerContext = useMemo(() => {
    const invalidDetails = invalidEdges.map((edge) => compactEdgeForAi(edge, sourceFieldById, entityFieldById));
    return {
      payloadKind: "EntityFieldMappingDesignerContextV1",
      entityObjectId: entityObject?.id || null,
      entityObject: {
        id: entityObject?.id || null,
        displayName: entityObjectDraftRaw?.DisplayName || entityObject?.displayName || "",
        objectName: entityObject?.objectName || "",
        fields: asArray(entityFields).map(compactFieldForAi),
      },
      activeSourceId,
      sourceObjectId: activeSourceId,
      activeSource: {
        id: activeSource?.id || null,
        displayName: activeSource?.displayName || "",
        objectName: activeSource?.objectName || "",
        fields: asArray(activeSource?.fields).map(compactFieldForAi),
      },
      allowMultiSource,
      hasUnsavedChanges,
      changedMapSourceIds,
      currentDraft: toDraftMap(activeMap),
      activeMappings: activeEdges.map((edge) => compactEdgeForAi(edge, sourceFieldById, entityFieldById)),
      invalidMappingCount: invalidEdges.length,
      invalidMappings: invalidDetails,
      selectedEdge: selectedMappingEdge ? compactEdgeForAi(selectedMappingEdge, sourceFieldById, entityFieldById) : null,
      selectedSourceField: selectedMappingEdge?.sourceFieldId ? compactFieldForAi(sourceFieldById.get(selectedMappingEdge.sourceFieldId)) : null,
      selectedEntityField: selectedMappingEdge?.entityFieldId ? compactFieldForAi(entityFieldById.get(selectedMappingEdge.entityFieldId)) : null,
      searchSource,
      searchEntity,
    };
  }, [
    activeEdges,
    activeMap,
    activeSource,
    activeSourceId,
    allowMultiSource,
    changedMapSourceIds,
    entityFieldById,
    entityFields,
    entityObject,
    entityObjectDraftRaw,
    hasUnsavedChanges,
    invalidEdges,
    searchEntity,
    searchSource,
    selectedMappingEdge,
    sourceFieldById,
  ]);

  useEffect(() => {
    if (typeof onAiContextChange !== "function") return;
    let contextText = "";
    try {
      contextText = JSON.stringify(aiDesignerContext);
    } catch {
      contextText = "";
    }
    if (contextText && contextText === lastAiContextRef.current) return;
    lastAiContextRef.current = contextText;
    onAiContextChange(aiDesignerContext);
  }, [aiDesignerContext, onAiContextChange]);

  useEffect(() => {
    const nextSourceId = asArray(sourceObjects).some((source) => source?.id === selectedSearchSourceId)
      ? selectedSearchSourceId
      : activeSourceId || asArray(sourceObjects)[0]?.id || "";
    if (nextSourceId !== selectedSearchSourceId) {
      setSelectedSearchSourceId(nextSourceId);
    }
  }, [sourceObjects, activeSourceId, selectedSearchSourceId]);

  const searchMappingSource = useMemo(
    () => asArray(sourceObjects).find((source) => source?.id === selectedSearchSourceId) || null,
    [sourceObjects, selectedSearchSourceId]
  );

  const searchMappingSourceFieldById = useMemo(
    () => new Map(asArray(searchMappingSource?.fields).map((field) => [field.id, field])),
    [searchMappingSource]
  );

  const searchMappingItems = useMemo(() => {
    const map = mapsBySourceId[selectedSearchSourceId] || buildEmptyFieldMap(entityObject?.id, selectedSearchSourceId);
    return asArray(map?.items).filter(
      (edge) =>
        !isDeletedEdge(edge) &&
        hasRenderableSource(edge, searchMappingSourceFieldById) &&
        hasRenderableEntity(edge, entityFieldById)
    );
  }, [mapsBySourceId, selectedSearchSourceId, entityObject?.id, searchMappingSourceFieldById, entityFieldById]);

  const searchMappingOptions = useMemo(
    () =>
      searchMappingItems.map((edge) => ({
        Value: edge.id,
        Text: buildMappingSearchLabel(edge, searchMappingSourceFieldById, entityFieldById),
        Important: isActiveEdge(edge),
        Sequence: -1,
      })),
    [searchMappingItems, searchMappingSourceFieldById, entityFieldById]
  );

  const selectedSearchMapping = useMemo(
    () => searchMappingItems.find((edge) => edge.id === selectedSearchEdgeId) || null,
    [searchMappingItems, selectedSearchEdgeId]
  );

  useEffect(() => {
    if (searchMappingOptions.length === 0) {
      if (selectedSearchEdgeId) setSelectedSearchEdgeId("");
      return;
    }
    if (!searchMappingOptions.some((item) => item.Value === selectedSearchEdgeId)) {
      setSelectedSearchEdgeId(searchMappingOptions[0]?.Value || "");
    }
  }, [searchMappingOptions, selectedSearchEdgeId]);

  useEffect(() => {
    if (!pendingOpenMapping) return;
    if (activeSourceId !== pendingOpenMapping.sourceId) return;
    const edge = asArray((mapsBySourceId[pendingOpenMapping.sourceId] || {}).items).find(
      (item) => item?.id === pendingOpenMapping.edgeId && isActiveEdge(item)
    );
    if (!edge) return;
    setSelectedEdgeId(edge.id);
    setTransformEdgeId(edge.id);
    setPendingOpenMapping(null);
  }, [pendingOpenMapping, activeSourceId, mapsBySourceId]);

  useEffect(() => {
    if (!pendingSelectMapping) return;
    if (activeSourceId !== pendingSelectMapping.sourceId) return;
    const edge = asArray((mapsBySourceId[pendingSelectMapping.sourceId] || {}).items).find(
      (item) => item?.id === pendingSelectMapping.edgeId && isActiveEdge(item)
    );
    if (!edge) {
      setPendingSelectMapping(null);
      return;
    }
    setSelectedEdgeId(edge.id);
    setPendingSelectMapping(null);
  }, [pendingSelectMapping, activeSourceId, mapsBySourceId]);

  const searchMappingSourceOptions = useMemo(
    () =>
      asArray(sourceObjects)
        .slice()
        .sort((left, right) => String(left?.displayName || "").localeCompare(String(right?.displayName || "")))
        .map((source) => ({
          Value: source.id,
          Text: source.displayName || source.name || source.id,
          Sequence: -1,
        })),
    [sourceObjects]
  );

  const setMapForSource = useCallback(
    (sourceId, updater) => {
      setMapsBySourceId((previous) => {
        const current = previous[sourceId] || buildEmptyFieldMap(entityObject?.id, sourceId);
        const next = typeof updater === "function" ? updater(current) : updater;
        const key = draftKey(entityObject?.id, sourceId, next.id || "new");
        try {
          localStorage.setItem(key, JSON.stringify(toDraftMap(next)));
        } catch (error) {
          try {
            localStorage.removeItem(key);
          } catch {
            // Ignore storage cleanup failures.
          }
          console.warn("Failed to persist mapping draft to localStorage.", error);
        }
        return { ...previous, [sourceId]: next };
      });
    },
    [entityObject?.id]
  );

  const setActiveMap = useCallback(
    (updater) => {
      setMapForSource(activeSourceId, updater);
    },
    [activeSourceId, setMapForSource]
  );

  const saveNow = useCallback(async () => {
    const sourceById = new Map(asArray(sourceObjects).map((x) => [x?.id, x]));
    const changedSourceIds = changedMapSourceIds;
    const hasFieldChanges = JSON.stringify(asArray(entityFields)) !== JSON.stringify(asArray(savedEntityFields));
    const hasObjectChanges = JSON.stringify(entityObjectDraftRaw || {}) !== JSON.stringify(savedEntityObjectRaw || {});

    if (changedSourceIds.length > 0) {
      const nextMaps = { ...mapsBySourceId };
      const nextSaved = { ...savedBySourceId };

      for (const sourceId of changedSourceIds) {
        const current = mapsBySourceId[sourceId];
        const savedMap = savedBySourceId[sourceId];
        if (!current) continue;

        const sourceFieldLookup = new Map(asArray(sourceById.get(sourceId)?.fields).map((field) => [field.id, field]));
        const invalidForMap = asArray(current?.items).filter((edge) => isActiveEdge(edge) && edgeInvalid(edge, sourceFieldLookup, entityFieldById));
        if (invalidForMap.length > 0) return;

        const result = await onSaveMap(current, { previousMap: savedMap });
        if (!result?.Success) return;

        const nextSavedMap = result.map || current;
        nextMaps[sourceId] = cloneMap(nextSavedMap);
        nextSaved[sourceId] = cloneMap(nextSavedMap);
        const key = draftKey(entityObject?.id, sourceId, nextSavedMap.id || "new");
        localStorage.removeItem(key);
      }

      setMapsBySourceId(nextMaps);
      setSavedBySourceId(nextSaved);
    }

    if (hasFieldChanges || hasObjectChanges) {
      const result = await onEntityFieldUpdate(asArray(entityFields), entityObjectDraftRaw);
      if (!result?.Success) return;
      setSavedEntityFields(cloneFields(entityFields));
      setSavedEntityObjectRaw(entityObjectDraftRaw || {});
      setPendingFieldPatches({});
    }

    setSavedEntityFieldOrder(asArray(entityFields).map((field) => field.id));
    setDraftRestored(false);
  }, [
    entityObject?.id,
    mapsBySourceId,
    savedBySourceId,
    sourceObjects,
    changedMapSourceIds,
    entityFieldById,
    onSaveMap,
    onEntityFieldUpdate,
    entityFields,
    savedEntityFields,
    entityObjectDraftRaw,
    savedEntityObjectRaw,
  ]);

  const scheduleAutoSave = useCallback(() => {}, []);

  useEffect(() => {
    if (!aiDraftUpdate?.version || lastAiDraftUpdateRef.current === aiDraftUpdate.version) return;
    const targetSourceId = aiDraftUpdate.sourceObjectId || activeSourceId;
    if (!targetSourceId) return;
    const nextItems = asArray(aiDraftUpdate.items);
    lastAiDraftUpdateRef.current = aiDraftUpdate.version;
    if (nextItems.length === 0) return;

    setMapForSource(targetSourceId, (previous) => ({
      ...previous,
      items: ensureUniqueMapItemIds([
        ...asArray(previous.items).filter((item) => !isActiveEdge(item)),
        ...nextItems.map((item) => ({
          ...item,
          sourceObjectId: item?.sourceObjectId || targetSourceId,
          isActive: true,
          isDeleted: false,
        })),
      ]),
    }));
    setActiveSourceId(targetSourceId);
    setSelectedEdgeId(nextItems[0]?.id || null);
    scheduleAutoSave();
  }, [activeSourceId, aiDraftUpdate, scheduleAutoSave, setMapForSource]);

  const computeLayout = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const nextLayout = {};
    activeEdges.forEach((edge) => {
      const entityRef = entityRowRefs.current[edge.entityFieldId];
      if (!entityRef) return;
      const eRect = entityRef.getBoundingClientRect();
      const isConstant = edge?.isConstant === true || edge?.sourceFieldId === EMPTY_GUID;

      let sx;
      let sy;
      if (isConstant) {
        const gutterRect = gutterRef.current?.getBoundingClientRect();
        sx = (gutterRect ? (gutterRect.left + gutterRect.right) / 2 : rect.left + rect.width / 2) - rect.left + scrollLeft;
        sy = (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop;
      } else {
        const sourceRef = sourceRowRefs.current[edge.sourceFieldId];
        if (!sourceRef) return;
        const sRect = sourceRef.getBoundingClientRect();
        sx = sRect.right - rect.left + scrollLeft;
        sy = (sRect.top + sRect.bottom) / 2 - rect.top + scrollTop;
      }
      nextLayout[edge.id] = {
        sx,
        sy,
        tx: eRect.left - rect.left + scrollLeft,
        ty: (eRect.top + eRect.bottom) / 2 - rect.top + scrollTop,
      };
    });
    setLayout(nextLayout);
  }, [activeEdges]);

  useEffect(() => {
    computeLayout();
    const handleResize = () => computeLayout();
    const canvas = canvasRef.current;
    const handleScroll = () => computeLayout();
    window.addEventListener("resize", handleResize);
    if (canvas) canvas.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (canvas) canvas.removeEventListener("scroll", handleScroll);
    };
  }, [computeLayout]);

  const beginSourceDrag = (field, event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const sourceRef = sourceRowRefs.current[field.id];
    if (!canvas || !sourceRef) return;
    const canvasRect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const sRect = sourceRef.getBoundingClientRect();
    const sx = sRect.right - canvasRect.left + scrollLeft;
    const sy = (sRect.top + sRect.bottom) / 2 - canvasRect.top + scrollTop;
    setDragging({ mode: "source", sourceFieldId: field.id, sx, sy, tx: sx, ty: sy });
  };

  const beginEntityDrag = (field, event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const entityRef = entityRowRefs.current[field.id];
    if (!canvas || !entityRef) return;
    const canvasRect = canvas.getBoundingClientRect();
    const scrollLeft = canvas.scrollLeft || 0;
    const scrollTop = canvas.scrollTop || 0;
    const eRect = entityRef.getBoundingClientRect();
    const sx = eRect.left - canvasRect.left + scrollLeft;
    const sy = (eRect.top + eRect.bottom) / 2 - canvasRect.top + scrollTop;
    setDragging({ mode: "entity", entityFieldId: field.id, sx, sy, tx: sx, ty: sy });
  };

  useEffect(() => {
    const onMove = (event) => {
      if (!dragging || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();

      // Auto-scroll upward while dragging near the top edge.
      const EDGE_THRESHOLD = 52;
      const MAX_STEP = 18;
      const pointerY = event.clientY - canvasRect.top;
      if (pointerY < EDGE_THRESHOLD && canvas.scrollTop > 0) {
        const ratio = (EDGE_THRESHOLD - Math.max(pointerY, 0)) / EDGE_THRESHOLD;
        const step = Math.max(2, Math.round(MAX_STEP * ratio));
        canvas.scrollTop = Math.max(0, canvas.scrollTop - step);
      }

      const scrollLeft = canvas.scrollLeft || 0;
      const scrollTop = canvas.scrollTop || 0;
      setDragging((previous) => ({
        ...previous,
        tx: event.clientX - canvasRect.left + scrollLeft,
        ty: event.clientY - canvasRect.top + scrollTop,
      }));
    };
    const onUp = (event) => {
      if (!dragging) return;

      if (dragging.mode === "source") {
        let targetEntityId = null;
        Object.keys(entityRowRefs.current).forEach((id) => {
          const element = entityRowRefs.current[id];
          if (!element) return;
          const rect = element.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
            targetEntityId = id;
          }
        });
        if (targetEntityId) {
          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: dragging.sourceFieldId,
            sourceObjectId: activeSourceId,
            entityFieldId: targetEntityId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "",
            importance: 255,
            isConstant: false,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
        }
      } else if (dragging.mode === "entity") {
        let targetSourceId = null;
        Object.keys(sourceRowRefs.current).forEach((id) => {
          const element = sourceRowRefs.current[id];
          if (!element) return;
          const rect = element.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
            targetSourceId = id;
          }
        });
        if (targetSourceId) {
          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: targetSourceId,
            sourceObjectId: activeSourceId,
            entityFieldId: dragging.entityFieldId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "",
            importance: 255,
            isConstant: false,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
          setDragging(null);
          return;
        }

        const gutterRect = gutterRef.current?.getBoundingClientRect();
        const droppedInGutter = !!(
          gutterRect &&
          event.clientX >= gutterRect.left &&
          event.clientX <= gutterRect.right &&
          event.clientY >= gutterRect.top &&
          event.clientY <= gutterRect.bottom
        );
        if (droppedInGutter) {
          const nextItem = {
            id: `item_${Math.random().toString(36).slice(2)}`,
            sourceFieldId: EMPTY_GUID,
            sourceObjectId: activeSourceId,
            entityFieldId: dragging.entityFieldId,
            transformKey: null,
            transformCode: "",
            transformDisplayName: "Constant",
            importance: 255,
            isConstant: true,
            isActive: true,
            isDeleted: false,
          };
          setActiveMap((previous) => ({
            ...previous,
            items: replaceMapping(previous.items, nextItem, { allowMultipleEntitiesPerSourceField: true }),
          }));
          setSelectedEdgeId(nextItem.id);
          scheduleAutoSave();
        }
      }
      setDragging(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, setActiveMap, scheduleAutoSave]);

  useEffect(() => {
    computeLayout();
  }, [computeLayout, sourceFields, filteredEntityFields, activeEdges.length]);

  const handleDeleteEdge = (edgeId) => {
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => {
        if (item.id !== edgeId) return item;
        return {
          ...item,
          isActive: false,
          isDeleted: false,
        };
      }),
    }));
    setSelectedEdgeId(null);
    setTransformEdgeId((previous) => (previous === edgeId ? null : previous));
    scheduleAutoSave();
  };

  const handleActivateSearchMapping = () => {
    if (!selectedSearchMapping || !selectedSearchSourceId) return;
    setMapForSource(selectedSearchSourceId, (previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => {
        const conflicts =
          item.id !== selectedSearchMapping.id &&
          isActiveEdge(item) &&
          (
            item.entityFieldId === selectedSearchMapping.entityFieldId ||
            (!selectedSearchMapping.isConstant && item.sourceFieldId === selectedSearchMapping.sourceFieldId)
          );
        if (conflicts) {
          return {
            ...item,
            isActive: false,
            isDeleted: false,
          };
        }
        if (item.id !== selectedSearchMapping.id) return item;
        return {
          ...item,
          isActive: true,
          isDeleted: false,
        };
      }),
    }));
    setActiveSourceId(selectedSearchSourceId);
    setSelectedSearchEdgeId(selectedSearchMapping.id);
    setSelectedEdgeId(selectedSearchMapping.id);
    scheduleAutoSave();
  };

  const handleOpenSearchMapping = () => {
    if (!selectedSearchMapping || !isActiveEdge(selectedSearchMapping) || !selectedSearchSourceId) return;
    if (activeSourceId !== selectedSearchSourceId) {
      setPendingOpenMapping({ sourceId: selectedSearchSourceId, edgeId: selectedSearchMapping.id });
      setActiveSourceId(selectedSearchSourceId);
      return;
    }
    setSelectedEdgeId(selectedSearchMapping.id);
    setTransformEdgeId(selectedSearchMapping.id);
  };

  const handleSelectSearchMapping = (value) => {
    const nextId = value || "";
    setSelectedSearchEdgeId(nextId);
    if (!nextId) {
      setSelectedEdgeId(null);
      return;
    }
    const edge = searchMappingItems.find((item) => item.id === nextId) || null;
    if (!edge || !isActiveEdge(edge)) {
      setSelectedEdgeId(null);
      return;
    }
    if (activeSourceId !== selectedSearchSourceId) {
      setPendingSelectMapping({ sourceId: selectedSearchSourceId, edgeId: edge.id });
      setActiveSourceId(selectedSearchSourceId);
      return;
    }
    setSelectedEdgeId(edge.id);
  };

  const handleTransformSave = (patch) => {
    setActiveMap((previous) => ({
      ...previous,
      items: asArray(previous.items).map((item) => (item.id === transformEdgeId ? { ...item, ...patch } : item)),
    }));
    setTransformEdgeId(null);
    scheduleAutoSave();
  };

  const rollback = () => {
    const saved = savedBySourceId[activeSourceId];
    if (saved) {
      setMapsBySourceId((previous) => ({ ...previous, [activeSourceId]: cloneMap(saved) }));
      const key = draftKey(entityObject?.id, activeSourceId, saved.id || "new");
      localStorage.removeItem(key);
    }
    setSelectedEdgeId(null);
    setTransformEdgeId(null);
    setDragging(null);
    setEntityFields((previous) => reorderByIdList(cloneFields(savedEntityFields), savedEntityFieldOrder.length > 0 ? savedEntityFieldOrder : previous.map((x) => x.id)));
    setEntityObjectDraftRaw(savedEntityObjectRaw || {});
    setPendingFieldPatches({});
    setDraftRestored(false);
  };

  const clearCurrentMap = () => {
    setActiveMap((previous) => ({
      ...previous,
      items: [],
    }));
    setSelectedEdgeId(null);
    setTransformEdgeId(null);
    setShowArchivedMappings(false);
    setDragging(null);
    scheduleAutoSave();
  };

  const handleSaveEntityField = async (patch) => {
    const fieldId = editField?.id;
    if (!fieldId) return { Success: false };
    const normalizedPatch = normalizeField({ ...(editField?.raw || {}), ...(patch || {}) });
    setEntityFields((previous) =>
      previous.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...normalizedPatch,
              id: field.id,
              raw: { ...(field.raw || {}), ...(patch || {}) },
            }
          : field
      )
    );
    setPendingFieldPatches((previous) => ({ ...previous, [fieldId]: { ...(previous[fieldId] || {}), ...(patch || {}) } }));
    setEditField(null);
    return { Success: true };
  };

  const applyAiSuggestion = async () => {
    if (!aiConfirm) return { Success: true };
    setActiveMap((previous) => ({
      ...previous,
        items: [
          ...asArray(previous.items).filter((item) => !isActiveEdge(item)),
          ...aiConfirm.items.map((item) => ({
            ...item,
            sourceObjectId: item?.sourceObjectId || activeSourceId,
            isActive: true,
            isDeleted: false,
          })),
        ],
      }));
    setAiConfirm(null);
    return { Success: true };
  };

  const runAiForField = async (sourceField) => {
    setAiFieldLoadingTarget(sourceField?.id ? { side: "source", id: sourceField.id } : null);
    try {
      const result = await onAiSuggestForField({
        entityObject,
        sourceObject: activeSource,
        sourceField,
        currentMap: activeMap,
      });
      if (!result?.Success) {
        setAiConfirm({
          items: activeEdges,
          summary: { added: 0, removed: 0, replaced: 0 },
          warnings: [String(result?.error || result?.DisplayMessage || "AI suggestion failed.")],
          canApprove: false,
        });
        return;
      }
      const suggested = asArray(result.items);
      const summary = mapDiff(activeEdges, suggested);
      const warnings = suggested
        .filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById))
        .map((edge) => {
          const sf = sourceFieldById.get(edge.sourceFieldId);
          const ef = entityFieldById.get(edge.entityFieldId);
          return `${sf?.displayName || sf?.fieldName} -> ${ef?.displayName || ef?.fieldName} requires transform`;
        });
      setAiConfirm({ items: suggested, summary, warnings: [...asArray(result?.warnings), ...warnings] });
    } finally {
      setAiFieldLoadingTarget(null);
    }
  };

  const runAiForEntityField = async (entityField) => {
    setAiFieldLoadingTarget(entityField?.id ? { side: "entity", id: entityField.id } : null);
    try {
      const result = await onAiSuggestForEntityField({
        entityObject,
        sourceObject: activeSource,
        entityField,
        currentMap: activeMap,
      });
      if (!result?.Success) {
        setAiConfirm({
          items: activeEdges,
          summary: { added: 0, removed: 0, replaced: 0 },
          warnings: [String(result?.error || result?.DisplayMessage || "AI suggestion failed.")],
          canApprove: false,
        });
        return;
      }

      const suggested = asArray(result.items);
      const summary = mapDiff(activeEdges, suggested);
      const warnings = suggested
        .filter((edge) => edgeInvalid(edge, sourceFieldById, entityFieldById))
        .map((edge) => {
          const sf = sourceFieldById.get(edge.sourceFieldId);
          const ef = entityFieldById.get(edge.entityFieldId);
          return `${sf?.displayName || sf?.fieldName} -> ${ef?.displayName || ef?.fieldName} requires transform`;
        });
      setAiConfirm({ items: suggested, summary, warnings: [...asArray(result?.warnings), ...warnings] });
    } finally {
      setAiFieldLoadingTarget(null);
    }
  };

  const selectedEdge = activeEdges.find((x) => x.id === transformEdgeId) || null;

  useEffect(() => {
    if (transformEdgeId) setSelectedEdgeId(transformEdgeId);
  }, [transformEdgeId]);

  const moveEntityField = useCallback((dragId, targetId) => {
    if (!dragId || !targetId || dragId === targetId) return;
    setEntityFields((previous) => {
      const currentIndex = previous.findIndex((x) => x.id === dragId);
      const targetIndex = previous.findIndex((x) => x.id === targetId);
      if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) return previous;
      const next = previous.slice();
      const [moved] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  }, []);

  const startEntityReorder = (field, event, rowElement) => {
    if (isEntitySearchActive) return;
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", field.id);
      if (rowElement) event.dataTransfer.setDragImage(rowElement, 16, 16);
    }
    setReorderEntityFieldId(field.id);
  };

  const pressEntityReorder = (field) => {
    if (isEntitySearchActive) return;
    setReorderEntityFieldId(field.id);
  };

  const dragOverEntityRow = (targetField, event) => {
    if (isEntitySearchActive) return;
    event.preventDefault();
    if (event?.dataTransfer) event.dataTransfer.dropEffect = "move";
    const dragId = reorderEntityFieldId || event?.dataTransfer?.getData("text/plain");
    moveEntityField(dragId, targetField.id);
  };

  const dropOnEntityRow = (targetField, event) => {
    if (isEntitySearchActive) return;
    event.preventDefault();
    const dragId = reorderEntityFieldId || event?.dataTransfer?.getData("text/plain");
    if (!dragId || dragId === targetField.id) {
      setReorderEntityFieldId(null);
      return;
    }
    moveEntityField(dragId, targetField.id);
    setReorderEntityFieldId(null);
  };

  const endEntityReorder = () => {
    setReorderEntityFieldId(null);
  };

  const autoScrollWhileReordering = (event) => {
    if (!reorderEntityFieldId || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pointerY = event.clientY - rect.top;
    const threshold = 56;
    const maxStep = 20;

    if (pointerY < threshold && canvas.scrollTop > 0) {
      const ratio = (threshold - Math.max(pointerY, 0)) / threshold;
      const step = Math.max(2, Math.round(maxStep * ratio));
      canvas.scrollTop = Math.max(0, canvas.scrollTop - step);
      return;
    }

    const distanceFromBottom = rect.height - pointerY;
    const maxScrollTop = canvas.scrollHeight - canvas.clientHeight;
    if (distanceFromBottom < threshold && canvas.scrollTop < maxScrollTop) {
      const ratio = (threshold - Math.max(distanceFromBottom, 0)) / threshold;
      const step = Math.max(2, Math.round(maxStep * ratio));
      canvas.scrollTop = Math.min(maxScrollTop, canvas.scrollTop + step);
    }
  };

  return (
    <div className="efmd">
      <div className="efmd-toolbar">
        <ButtonControl type={"cancel"} onClick={() => setShowRollbackConfirm(true)} disabled={!hasDraftAvailable}>
          Rollback
        </ButtonControl>
        <ButtonControl type={"ok"} onClick={saveNow} disabled={!hasUnsavedChanges}>
          Save
        </ButtonControl>
        <ButtonControl type={"cancel"} onClick={() => setShowClearConfirm(true)} disabled={!hasMappingsToClear}>
          Clear
        </ButtonControl>
        <div className="efmd-toolbar__spacer" />
        <button type="button" className="efmd-toolbar__icon-btn" title="Edit Entity Object" onClick={() => setEditEntityObjectOpen(true)}>
          <IconControl iconKey={"edit"} />
        </button>
        <button
          type="button"
          className={`efmd-toolbar__icon-btn${showArchivedMappings ? " is-active" : ""}`}
          title={showArchivedMappings ? "Hide Search Mappings" : "Show Search Mappings"}
          onClick={() => setShowArchivedMappings((previous) => !previous)}
        >
          <IconControl iconKey={"viewmore"} />
        </button>
      </div>

      {showArchivedMappings && (
        <div className="efmd-toolbar efmd-toolbar--secondary">
          {allowMultiSource && (
            <div className="efmd-toolbar__search-field">
              <FieldControl
                className="efmd-toolbar__search-control efmd-toolbar__search-control--inline"
                fieldMetadata={buildSearchSelectMetadata({
                  fieldName: "searchMappingsSourceObjectId",
                  displayName: "Source Object",
                  dataJson: searchMappingSourceOptions,
                  placeHolder: "Select Source Object",
                })}
                value={selectedSearchSourceId || null}
                onChange={(value) => setSelectedSearchSourceId(value || "")}
              />
            </div>
          )}
          <div className="efmd-toolbar__search-field efmd-toolbar__search-field--wide">
            <FieldControl
              className="efmd-toolbar__search-control efmd-toolbar__search-control--inline"
              fieldMetadata={buildSearchSelectMetadata({
                fieldName: "searchMappingId",
                displayName: "Search Mappings",
                dataJson: searchMappingOptions,
                placeHolder: searchMappingOptions.length > 0 ? "Search Mappings" : "No Mappings",
              })}
              value={selectedSearchEdgeId || null}
              onChange={handleSelectSearchMapping}
              disabled={searchMappingOptions.length === 0}
            />
          </div>
          {selectedSearchMapping && isActiveEdge(selectedSearchMapping) ? (
            <ButtonControl type={"standard"} onClick={handleOpenSearchMapping}>
              Open
            </ButtonControl>
          ) : (
            <ButtonControl type={"standard"} onClick={handleActivateSearchMapping} disabled={!selectedSearchMapping}>
              Make Active
            </ButtonControl>
          )}
        </div>
      )}

      {invalidEdges.length > 0 && <div className="efmd-errors">{invalidEdges.length} invalid mapping(s): data type mismatch requires transform.</div>}
      <div className="efmd-head">
        <div className="efmd-head__left">
          <div className="efmd-head__label">Source</div>
          {allowMultiSource ? (
            <select value={activeSourceId} onChange={(event) => setActiveSourceId(event.target.value)} className="efmd-select">
              {asArray(sourceObjects).map((source) => (
                <option value={source.id} key={source.id}>
                  {source.displayName}
                </option>
              ))}
            </select>
          ) : (
            <div className="efmd-head__name">{activeSource?.displayName}</div>
          )}
          <SearchInput value={searchSource} onChange={setSearchSource} />
        </div>
        <div className="efmd-head__right">
          <div className="efmd-head__entity-spacer" aria-hidden="true" />
          <div className="efmd-head__label">Entity</div>
          <div className="efmd-head__name">{entityObjectDraftRaw?.DisplayName || entityObject?.displayName}</div>
          <SearchInput value={searchEntity} onChange={setSearchEntity} />
        </div>
      </div>

      <div className="efmd-canvas" ref={canvasRef} onDragOver={autoScrollWhileReordering}>
        <div className="efmd-list efmd-list--source">
          {sourceFields.map((field) => (
            <FieldRow
              key={field.id}
              side={"source"}
              field={field}
              isMapped={activeEdges.some((x) => x.sourceFieldId === field.id)}
              onStartConnect={beginSourceDrag}
              onQuickFilter={(clickedField) => {
                const term = String(clickedField?.fieldName || clickedField?.displayName || "").trim();
                setSearchSource((previous) => (String(previous || "").trim().toLowerCase() === term.toLowerCase() ? "" : term));
              }}
              onAiSuggestField={runAiForField}
              aiSuggestLoading={aiFieldLoadingTarget?.side === "source" && aiFieldLoadingTarget?.id === field.id}
              onEditEntityField={() => {}}
              setRef={(element) => {
                if (element) sourceRowRefs.current[field.id] = element;
                else delete sourceRowRefs.current[field.id];
              }}
            />
          ))}
        </div>
        <div className="efmd-gutter" ref={gutterRef} />
        <div className="efmd-list efmd-list--entity">
          {filteredEntityFields.map((field) => (
            <FieldRow
              key={field.id}
              side={"entity"}
              field={field}
              isMapped={activeEdges.some((x) => x.entityFieldId === field.id)}
              onStartConnect={beginEntityDrag}
              onQuickFilter={(clickedField) => {
                const term = String(clickedField?.fieldName || clickedField?.displayName || "").trim();
                setSearchEntity((previous) => (String(previous || "").trim().toLowerCase() === term.toLowerCase() ? "" : term));
              }}
              onAiSuggestField={runAiForEntityField}
              aiSuggestLoading={aiFieldLoadingTarget?.side === "entity" && aiFieldLoadingTarget?.id === field.id}
              onEditEntityField={(field) =>
                setEditField({
                  ...field,
                  raw: {
                    ...(field.raw || {}),
                    _id: field?.raw?._id || field.id,
                    UniqueId: field?.raw?.UniqueId || field.id,
                    EntityObjectFieldId: field.id,
                    FieldName: field.fieldName,
                    DisplayName: field.displayName,
                    Description: field.description,
                    AIPromptContext: field.aiPromptContext,
                    Format: field.format,
                    DataTypeId: field.dataTypeId,
                    DataTypeName: field.dataTypeName,
                    IsActive: field.isActive,
                  },
                })
              }
              onStartReorder={startEntityReorder}
              onPressReorder={pressEntityReorder}
              onReleaseReorder={endEntityReorder}
              onDragOverRow={dragOverEntityRow}
              onDropOnRow={dropOnEntityRow}
              onEndReorder={endEntityReorder}
              isReordering={reorderEntityFieldId === field.id}
              disableReorder={isEntitySearchActive}
              setRef={(element) => {
                if (element) entityRowRefs.current[field.id] = element;
                else delete entityRowRefs.current[field.id];
              }}
            />
          ))}
        </div>

        <MappingOverlay
          edges={activeEdges}
          layout={layout}
          selectedEdgeId={selectedEdgeId}
          onSelectEdge={setSelectedEdgeId}
          onOpenTransform={setTransformEdgeId}
          onDeleteEdge={handleDeleteEdge}
          isEdgeInvalid={(edge) => edgeInvalid(edge, sourceFieldById, entityFieldById)}
          draggingLine={dragging}
        />
      </div>
      <div className="efmd-footnote">
        <strong>Tips:</strong> Drag Source: Entity to map fields. Drag Entity: gutter (middle) to create a constant mapping. Double-click a mapping line to open its transform settings.
      </div>

      <EntityFieldEditDialog
        open={!!editField}
        field={editField}
        dataTypes={dataTypes}
        fieldItemMetadata={fieldItemMetadata}
        onCancel={() => setEditField(null)}
        onSave={handleSaveEntityField}
      />

      <EntityObjectEditDialog
        open={editEntityObjectOpen}
        entityObject={{
          ...entityObject,
          raw: entityObjectDraftRaw || entityObject?.raw || {},
          displayName: entityObjectDraftRaw?.DisplayName || entityObject?.displayName,
        }}
        entityObjectItemMetadata={entityObjectItemMetadata}
        onCancel={() => setEditEntityObjectOpen(false)}
        onSave={async (patch) => {
          setEntityObjectDraftRaw((previous) => ({
            ...(previous || {}),
            ...(patch || {}),
            _id: previous?._id || entityObject?.id,
            EntityObjectId: previous?.EntityObjectId || entityObject?.id,
          }));
          setEditEntityObjectOpen(false);
          return { Success: true };
        }}
      />

      <TransformToolkitDialog
        open={!!transformEdgeId}
        mapping={selectedEdge}
        sourceField={sourceFieldById.get(selectedEdge?.sourceFieldId)}
        entityField={entityFieldById.get(selectedEdge?.entityFieldId)}
        sourceObjects={lookupSourceObjects}
        entityObjects={lookupEntityObjects}
        sourceObjectFieldMapItemMetadata={sourceObjectFieldMapItemMetadata}
        sourceObjectFieldTransformScriptItemMetadata={sourceObjectFieldTransformScriptItemMetadata}
        hideImportance={isSingleSourceOnly}
        transforms={transformCatalog}
        onCancel={() => setTransformEdgeId(null)}
        onDelete={() => {
          handleDeleteEdge(transformEdgeId);
          setTransformEdgeId(null);
        }}
        onSave={handleTransformSave}
      />

      <AiMappingConfirmDialog
        open={!!aiConfirm}
        summary={aiConfirm?.summary}
        warnings={aiConfirm?.warnings}
        canApprove={aiConfirm?.canApprove !== false}
        onCancel={() => setAiConfirm(null)}
        onConfirm={applyAiSuggestion}
      />

      <DialogControl
        openDialog={showClearConfirm}
        title={"Confirm Clear"}
        onX={() => setShowClearConfirm(false)}
        onCancel={() => setShowClearConfirm(false)}
        onOk={() => {
          clearCurrentMap();
          setShowClearConfirm(false);
        }}
        cancelText={"Cancel"}
        okText={"Clear"}
      >
        <div className="efmd-dialog">
          This will clear all mappings for the current source object from the designer. Save afterward to persist the removal.
        </div>
      </DialogControl>

      <DialogControl
        openDialog={showRollbackConfirm}
        title={"Confirm Rollback"}
        onX={() => setShowRollbackConfirm(false)}
        onCancel={() => setShowRollbackConfirm(false)}
        onOk={() => {
          rollback();
          setShowRollbackConfirm(false);
        }}
        cancelText={"Cancel"}
        okText={"Rollback"}
      >
        <div className="efmd-dialog">
          All unsaved changes will be lost, including field mappings, transform edits, and entity field updates. This action cannot be undone.
        </div>
      </DialogControl>
    </div>
  );
};

export default EntityFieldMapDesigner;
